import Avatar from "../ds/Avatar";
import Badge from "../ds/Badge";
import ProgressBar from "../ds/ProgressBar";
import UsernameLink from "../UsernameLink";
import CardMedia from "./CardMedia";
import { STATUS_KEYS, STATUS_TONES, campaignRateText } from "./campaignDisplay";
import { formatCampaignDate, formatCampaignMoney, getParticipantTypeLabel } from "../../utils/campaignCardUtils";
import { getProfileAvatarUrl } from "../../utils/userDisplay";

/*
  Figma "Campaign card" (B1 · აღმოჩენა): cover with the type badge, rate per
  1000 views, name, organizer, paid out against the budget, status and start date.
*/
export const CARD_SURFACE =
  "relative flex h-full w-full flex-col overflow-hidden rounded-radius-lg bg-[linear-gradient(140deg,var(--color-bg-raised),var(--color-bg-raised-end))] shadow-raised outline-none focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)]";

const DiscoverCampaignCard = ({ campaign, onOpen, isSelected = false, t }) => {
  const media = campaign.thumbnail || campaign.campaignLogo || "";
  const isVideo = Boolean(media) && (campaign.thumbnailType || "image") === "video";
  const spent = Number(campaign.spent) || 0;
  const budget = Number(campaign.budget) || 0;
  const owner = campaign.createdBy;
  const open = () => onOpen(campaign);

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === "Enter") open();
      }}
      className={`${CARD_SURFACE} cursor-pointer ${isSelected ? "outline outline-1 -outline-offset-1 outline-accent-default" : ""}`}
    >
      <CardMedia
        src={media}
        isVideo={isVideo}
        placeholderIcon="megaphone"
        placeholderLabel={t("home.mediaCampaign")}
        className="aspect-[354/203]"
      >
        <span className="absolute left-space-12 top-space-12 inline-flex h-[24px] items-center rounded-radius-sm bg-fill-secondary px-space-8 text-caps-button-s uppercase text-label-primary">
          {getParticipantTypeLabel(t, campaign.participantType)}
        </span>
      </CardMedia>

      <div className="flex flex-1 flex-col gap-space-12 p-space-16">
        <p className="flex items-baseline gap-[6px] whitespace-nowrap">
          <span className="text-title-2 text-label-primary">{campaignRateText(campaign)}</span>
          <span className="text-subheadline text-label-secondary">{t("home.per1000Views")}</span>
        </p>
        <h3 className="line-clamp-2 text-headline text-label-primary">{campaign.campaignName}</h3>
        {owner && (
          <div className="flex min-w-0 items-center gap-space-8">
            <Avatar src={getProfileAvatarUrl(owner)} name={owner.username} />
            <UsernameLink user={owner} stopPropagation className="min-w-0 truncate text-footnote text-label-secondary" />
          </div>
        )}
        <div className="flex flex-col gap-[6px]">
          <div className="flex items-start gap-space-4">
            <span className="flex-1 text-caps-label uppercase text-label-secondary">{t("home.paidOut")}</span>
            <span className="whitespace-nowrap text-footnote-semibold text-label-primary">
              {formatCampaignMoney(spent)} ₾{budget > 0 ? ` / ${formatCampaignMoney(budget)} ₾` : ""}
            </span>
          </div>
          <ProgressBar value={budget > 0 ? spent / budget : 0} />
        </div>
        <div className="mt-auto flex min-w-0 items-center gap-space-8">
          <Badge size="s" tone={STATUS_TONES[campaign.status] || "warning"}>
            {t(STATUS_KEYS[campaign.status] || STATUS_KEYS.pending)}
          </Badge>
          <span className="truncate text-footnote text-label-secondary">{formatCampaignDate(t, campaign.startDate)}</span>
        </div>
      </div>
    </div>
  );
};

export default DiscoverCampaignCard;
